import { createRouter, createWebHistory } from 'vue-router'
import homePageMngRoutes from '@/routes/cmsMng/homePageMng'
import boardMngRoutes from '@/routes/cmsMng/boardMng'
import unitProgramRoutes from '@/routes/cmsMng/unitProgram'
import configSettingRoutes from '@/routes/cmsMng/configSetting'
import resvMng from '@/routes/resvMng'
import reportMng from '@/routes/reportMng'
import etcMng from '@/routes/etcMng'
import { getAccessTokenInfoCheck } from '@/service/auth.service'
import { verifyClientIp } from '@/service/ipVerify.service'

const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return {}
  }
}

const hasAllowedRole = (roles) => {
  if (!Array.isArray(roles)) return false
  return roles.includes('ROLE_EMP') || roles.includes('ROLE_ADMIN')
}

const hasInstitutionManagerMapping = (payload) => {
  return payload?.institution_manager_mapped === true
}

const routes = [
  {
    path: '/',
    name: 'Home',
    meta: { layout: 'main' },
    component: () => import('@/pages/Home.vue')
  },
  {
    path: '/login',
    name: 'Login',
    meta: { layout: 'blank' },
    component: () => import('@/pages/login/LoginView.vue')
  },
  {
    path: '/popup/juso-address',
    name: 'JusoAddressPopup',
    meta: { layout: 'blank' },
    component: () => import('@/pages/popup/JusoAddressPopup.vue')
  },
  {
    path: '/popup/instr-change-pw',
    name: 'InstrChangePwPopup',
    meta: { layout: 'blank' },
    component: () => import('@/pages/login/popup/InstrChangePwPopup.vue')
  },
  { // 예약관리
    path: '/resv-mng',
    meta: { layout: 'default' },
    children: [
      ...resvMng
    ]
  },
  { // 홈페이지 관리
    path: '/homepage-mng',
    redirect: { name: 'MenuList' },
    meta: { layout: 'default' },
    children: [
      ...homePageMngRoutes
    ]
  },
  { // 게시판 관리
    path: '/board-mng',
    redirect: { name: 'BoardAttrList' },
    meta: { layout: 'default' },
    children: [
      ...boardMngRoutes
    ]
  },
  { // 단위 프로그램
    path: '/unit-program',
    redirect: { name: 'InstList' },
    meta: { layout: 'default' },
    children: [
      ...unitProgramRoutes
    ]
  },
  { // 환경설정
    path: '/config-setting',
    redirect: { name: 'CmmCodeList' },
    meta: { layout: 'default' },
    children: [
      ...configSettingRoutes
    ]
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    meta: { layout: 'default' },
    component: () => import('@/pages/etcMng/member/ChangePasswordView.vue')
  },
  { // 접근 차단
    path: '/access-denied',
    name: 'AccessDenied',
    meta: { layout: 'blank' },
    component: () => import('@/pages/AccessDenied.vue')
  },
  { // 404 Not Found
    path: '/notFound',
    name: 'NotFound',
    meta: { layout: 'blank' },
    component: () => import('@/pages/NotFound.vue')
  },
  { // 헬스체크 페이지
    path: '/actuator/health',
    name: 'Health',
    meta: { layout: 'blank' },
    component: () => import('@/pages/Health.vue')
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/notFound'
  },
  // 트리아이템 테스트
  {
    path: '/test/tree-item',
    name: 'TreeItemTest',
    meta: { layout: 'blank' },
    component: () => import('@/pages/test/TreeItem.vue')
  },
  // 리포트 테스트
  {
    path: '/report-example',
    name: 'ReportExample',
    meta: { layout: 'blank' },
    component: () => import('@/pages/test/ReportExample.vue')
  },
  {
    path: '/test/juso-coordinate-map',
    name: 'JusoCoordinateMapTest',
    meta: { layout: 'blank' },
    component: () => import('@/pages/test/JusoCoordinateMapView.vue')
  },
  // 영업일보
  {
    path: '/report-mng/',
    redirect: { name: 'TotalSalesStatusReportList' },
    meta: { layout: 'default' },
    children: [
      ...reportMng
    ]
  },
  { // 기타관리
    path: '/etc-mng',
    meta: { layout: 'default' },
    children: [
      ...etcMng
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

// 인증 없이 접근 가능한 경로
const publicPaths = ['/login', '/notFound', '/access-denied', '/actuator/health', '/popup/juso-address', '/popup/instr-change-pw']

// IP 검증 결과 캐시 (세션 내 1회만 검증)
let ipVerified = null

/**
 * 인증 가드 - 토큰 없거나 세션 만료 시 로그인 페이지로 리다이렉트
 */
router.beforeEach(async (to, from, next) => {
  // 팝업(자식 window)에서 라우팅할 때 __layout=blank를 항상 유지
  const isPopup = typeof window !== 'undefined' && !!window.opener && !window.opener.closed

  // 팝업은 인증 체크 없이 __layout=blank만 유지
  if (isPopup) {
    if (to.query?.__layout !== 'blank') {
      return next({
        ...to,
        query: { ...to.query, __layout: 'blank' },
        replace: true
      })
    }
    return next()
  }

  // 접근 차단 페이지는 바로 통과
  if (to.path === '/access-denied') {
    return next()
  }

  // IP 검증 (세션 내 1회만 수행)
  if (ipVerified === null) {
    try {
      ipVerified = await verifyClientIp()
    } catch {
      ipVerified = false
    }
  }

  if (!ipVerified) {
    return next('/access-denied')
  }

  // 공개 경로는 인증 없이 접근 가능
  const isPublicPath = publicPaths.some(path => to.path.startsWith(path))
  if (isPublicPath) {
    return next()
  }

  // 토큰 확인 (localStorage 사용 - 탭 간 공유)
  const accessToken = localStorage.getItem('accessToken')
  const instId = localStorage.getItem('SELECTED_INST')
  const sessionExpireAt = Number(localStorage.getItem('sessionExpireAt'))
  
  // 토큰이 없는 경우: 첫 방문 또는 로그아웃 상태 → 조용히 로그인으로 이동
  if (!accessToken) {
    return next('/login')
  }

  // 토큰은 있지만 기관이 선택되지 않은 경우 → 세션 정리 후 로그인으로 이동
  if (!instId) {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('idToken')
    localStorage.removeItem('SELECTED_INST')
    localStorage.removeItem('SELECTED_INST_NM')
    localStorage.removeItem('sessionExpireAt')
    localStorage.removeItem('userInfo')
    alert('세션이 만료되었습니다. 재로그인 바랍니다.')
    return next('/login')
  }

  // 역할 체크 (ROLE_EMP / ROLE_ADMIN 외에는 즉시 차단)
  const payload = parseJwt(accessToken)
  const roles = payload?.roles
  const isInstitutionManagerMapped = hasInstitutionManagerMapping(payload)
  if (!isInstitutionManagerMapped && !hasAllowedRole(roles)) {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('idToken')
    localStorage.removeItem('SELECTED_INST')
    localStorage.removeItem('SELECTED_INST_NM')
    localStorage.removeItem('sessionExpireAt')
    localStorage.removeItem('userInfo')
    alert('접근 권한이 없습니다.')
    return next('/login')
  }
  
  // 세션 만료 체크 (다른 탭에서도 만료 시 로그인 페이지로 이동)
  if (sessionExpireAt && Date.now() > sessionExpireAt) {
    // 세션 만료 - 모든 인증 정보 삭제
    localStorage.removeItem('accessToken')
    localStorage.removeItem('idToken')
    localStorage.removeItem('SELECTED_INST')
    localStorage.removeItem('SELECTED_INST_NM')
    localStorage.removeItem('sessionExpireAt')
    localStorage.removeItem('userInfo')
    
    alert('세션이 만료되었습니다. 재로그인 바랍니다.')
    return next('/login')
  }

  // 페이지 이동 시 토큰 상태 확인 (AUTO)
  try {
    const info = await getAccessTokenInfoCheck({
      accessToken,
      refreshMode: 'AUTO',
      tenant: 'res-admin'
    })
    const remainSeconds = info?.remainingSeconds ?? null
    if (remainSeconds === 0 || remainSeconds === null) {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('idToken')
      localStorage.removeItem('SELECTED_INST')
      localStorage.removeItem('SELECTED_INST_NM')
      localStorage.removeItem('sessionExpireAt')
      localStorage.removeItem('userInfo')
      alert('세션이 만료되었습니다. 재로그인 바랍니다.')
      return next('/login')
    }

    // 백단 기준 남은 시간으로 세션 만료 시각 갱신
    localStorage.setItem('sessionExpireAt', String(Date.now() + Math.floor(remainSeconds) * 1000))
  } catch (error) {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('idToken')
    localStorage.removeItem('SELECTED_INST')
    localStorage.removeItem('SELECTED_INST_NM')
    localStorage.removeItem('sessionExpireAt')
    localStorage.removeItem('userInfo')
    alert(error?.serverMessage ||'세션이 만료되었습니다. 재로그인 바랍니다.')
    return next('/login')
  }

  return next()
})


export default router


